import React, { useState, useEffect, useMemo, useCallback } from "react";
import { fetchMessageAPICall } from "../../env/APImanager";
import ClipLoader from "react-spinners/ClipLoader";

import AppBar from "../appbar/AppBar";
import Animation from "./Animation";
import PostCard from "./PostCard";
import "./Card.css";

/**
 * Classifies API errors and returns user-friendly error messages
 * @param {Error} error - The error object from the API call
 * @returns {{user: string, detail: string}} - User-friendly error messages
 */
const classifyErrorMessage = (error) => {
  let userMessage = 'Unable to load personal message.';
  let detailMessage = 'Please try again later.';

  if (error?.response) {
    // Server error (4xx, 5xx)
    const status = error.response.status;
    if (status >= 500) {
      userMessage = 'Server error';
      detailMessage = 'Our servers are experiencing issues. Please try again later.';
    } else if (status === 404) {
      userMessage = 'Message not found';
      detailMessage = 'This personal message may have been deleted or is no longer available.';
    } else if (status === 403 || status === 401) {
      userMessage = 'Access denied';
      detailMessage = 'You do not have permission to view this message.';
    } else {
      userMessage = 'Unable to load message';
      detailMessage = 'Please try again later.';
    }
  } else if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    // Timeout error
    userMessage = 'Request timed out';
    detailMessage = 'The request took too long. Please check your connection and try again.';
  } else if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network') || error?.message?.includes('fetch')) {
    // Network error
    userMessage = 'Network error';
    detailMessage = 'Please check your internet connection and try again.';
  }

  return { user: userMessage, detail: detailMessage };
};

const Card = () => {
  const [message, setMessage] = useState({});
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [fallbackAssets, setFallbackAssets] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Memoize URL params to avoid recalculating on every render
  const { tokenId, appbarDisplay, preview } = useMemo(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const appbar = params.get("appbar") || false;
    return {
      tokenId: params.get("tokenId"),
      appbarDisplay: appbar === "true",
      preview: params.get("preview") === "true",
    };
  }, []);

  const fetchMessage = useCallback(async () => {
    setLoadingMessage(true);
    setMessage({});
    setHasError(false);
    setErrorMessage(null);

    try {
      let currentMessage = await fetchMessageAPICall(tokenId);

      if (currentMessage == null) {
        // In preview mode, don't redirect - just show error state
        if (preview) {
          setErrorMessage({
            user: 'Message not found',
            detail: 'This personal message may have been deleted or is no longer available.'
          });
          setLoadingMessage(false);
          setHasError(true);
        } else {
          window.location.replace("https://gesture.vip");
        }
      } else {
        if (!currentMessage.designElements) {
          setFallbackAssets(true);
        }
        setMessage(currentMessage);
        setLoadingMessage(false);
      }
    } catch (error) {
      // Handle API errors gracefully
      // In preview mode (iframe), don't redirect - just show error state
      if (!preview) {
        console.error('Failed to fetch message:', error);
      }

      // Classify error type for user-friendly messaging
      const errorMessages = classifyErrorMessage(error);
      setErrorMessage(errorMessages);
      setLoadingMessage(false);
      setHasError(true);

      // Only redirect if not in preview mode (not in iframe)
      if (!preview) {
        window.location.replace("https://gesture.vip");
      }
    }
  }, [tokenId, preview]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMessage();
    document.body.classList.add("message-background");

    return () => {
      document.body.classList.remove("message-background");
    };
  }, [fetchMessage]);

  if (loadingMessage) {
    return (
      <>
        <section className="message-card-container">
          <div className="message-loading">
            <ClipLoader size={100} color="#2B08A0" />
          </div>
        </section>
      </>
    );
  }

  // Show error state in preview mode (iframe) when fetch fails
  if (hasError && preview) {
    const displayMessage = errorMessage?.user || 'Unable to load personal message.';
    const displayDetail = errorMessage?.detail || 'Please try again later.';

    return (
      <>
        <section className="message-card-container">
          <div className="message-error-container">
            <p className="message-error-title">{displayMessage}</p>
            <p className="message-error-text">
              {displayDetail}
            </p>
          </div>
        </section>
      </>
    );
  }

  // Don't render PostCard/Animation if message is empty (error state or not loaded)
  if (!message || Object.keys(message).length === 0) {
    return null;
  }

  return (
    <>
      <section className="message-card-container">
        {appbarDisplay && <AppBar />}
        {preview ? (
          <PostCard
            message={message}
            preview={true}
            fallbackAssets={fallbackAssets}
          />
        ) : (
          <Animation message={message} fallbackAssets={fallbackAssets} />
        )}
      </section>
    </>
  );
};

export default Card;
